import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { AnimatePresence, motion } from 'motion/react';
import { CheckCircleIcon, CopySimpleIcon, XCircleIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { swallowEvent, writeToClipboard } from '@admin-panel/utils';

type CopyStatus = 'idle' | 'success' | 'error';

type ButtonSize = 'small' | 'medium' | 'large';

interface ICopyToClipboardButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  // Omit drag events that conflict with Motion's onDrag handlers
  'onClick' | 'onDrag' | 'onDragStart' | 'onDragEnd' | 'onAnimationStart'
> {
  text: string;
  // Optional explicit icon size override. If not provided, adapts to `size`.
  iconSize?: number;
  resetDelay?: number;
  // Standardized control for button dimensions
  size?: ButtonSize;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export const CopyToClipboardButton = ({
  text,
  iconSize,
  size = 'medium',
  resetDelay = 2000,
  className,
  style,
  disabled,
  onClick,
  ...buttonProps
}: ICopyToClipboardButtonProps) => {
  const BUTTON_SIZES: Record<ButtonSize, number> = {
    small: 28,
    medium: 36,
    large: 44,
  };

  const ICON_SIZES: Record<ButtonSize, number> = {
    small: 16,
    medium: 20,
    large: 24,
  };

  const resolvedButtonSize = BUTTON_SIZES[size];
  const resolvedIconSize = iconSize ?? ICON_SIZES[size];

  const [status, setStatus] = useState<CopyStatus>('idle');
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearResetTimer = () => {
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  };

  useEffect(() => clearResetTimer, []);

  const iconNode =
    status === 'success' ? (
      <CheckCircleIcon size={resolvedIconSize} color="#16a34a" weight="fill" />
    ) : status === 'error' ? (
      <XCircleIcon size={resolvedIconSize} color="#dc2626" weight="fill" />
    ) : (
      <CopySimpleIcon size={resolvedIconSize} />
    );

  return (
    <IconButton
      {...buttonProps}
      type={buttonProps.type ?? 'button'}
      className={className}
      style={style}
      disabled={disabled}
      status={status}
      buttonsize={resolvedButtonSize}
      whileTap={{ scale: disabled ? 1 : 0.92 }}
      onClick={e => {
        swallowEvent(e);
        onClick?.(e);
        if (disabled) return;

        clearResetTimer();
        void (async () => {
          try {
            const didCopy = await writeToClipboard(text);
            if (didCopy) {
              setStatus('success');
              toast.success(`Copied ${text} to clipboard`);
            } else {
              setStatus('error');
              toast.error('Unable to copy to clipboard');
            }
          } catch {
            setStatus('error');
            toast.error('Unable to copy to clipboard');
          } finally {
            if (resetDelay) {
              resetTimerRef.current = setTimeout(() => setStatus('idle'), resetDelay);
            }
          }
        })();
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
        <IconWrapper
          key={status}
          iconSize={resolvedIconSize}
          initial={{ scale: 0.75, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.75, opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {iconNode}
        </IconWrapper>
      </AnimatePresence>
    </IconButton>
  );
};

export default CopyToClipboardButton;

const iconButtonShouldForwardProp = (prop: string) => !['buttonsize', 'status'].includes(prop);

const IconButton = styled(motion.button, {
  shouldForwardProp: iconButtonShouldForwardProp,
})<{
  buttonsize: number;
  disabled?: boolean;
  status: CopyStatus;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ buttonsize }) => buttonsize}px;
  height: ${({ buttonsize }) => buttonsize}px;
  border-radius: 50%;
  border: 1px solid ${({ status }) => (status === 'error' ? '#dc2626' : '#d9d9d9')};
  background-color: ${({ disabled }) => (disabled ? '#f5f5f5' : '#fff')};
  padding: 0;
  line-height: 0;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease;
  box-shadow: ${({ status }) =>
    status === 'success' ? '0 0 0 2px rgba(22, 163, 74, 0.2)' : 'none'};

  &:hover {
    border-color: ${({ disabled }) => (disabled ? '#d9d9d9' : '#1677ff')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.25);
  }

  flex-shrink: 0;
`;

const iconWrapperShouldForwardProp = (prop: string) => prop !== 'iconSize';

const IconWrapper = styled(motion.span, {
  shouldForwardProp: iconWrapperShouldForwardProp,
})<{ iconSize: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ iconSize }) => iconSize}px;
  height: ${({ iconSize }) => iconSize}px;
  line-height: 0;
`;
